import React, { useEffect, useMemo, useState } from "react";

import { HelperText } from "@common/components/HelperText/HelperText";
import {
  CrashReportTableRowItem,
  IssueTableRowItem,
  RowType,
} from "@common/components/MasterTable/models/MasterTableModel";
import { ModalHeader } from "@common/components/Modal/ModalHeader/ModalHeader";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { Spinner } from "@common/components/Spinner/Spinner";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { getStatusIcon } from "@common/util/getIconsHelper";
import useFilterByTitleConsumer from "@main/consumers/useFilterByTitleConsumer";

import * as Styled from "./ModalBody.styles";

export function MergeIntoModalBody<TData>({
  title,
  confirmAction,
  close,
  selectedRows,
  type,
  deselectRows,
}: {
  title: string;
  confirmAction: (rows: TData[], mergeRow: TData) => void;
  close: () => void;
  selectedRows: TData[];
  type?: RowType;
  deselectRows?: () => void;
}) {
  const { filter, filteredData, setFilteredData, loading } = useFilterByTitleConsumer({
    // eslint-disable-next-line
    // @ts-ignore
    row_type: type,
    // eslint-disable-next-line
    // @ts-ignore
    selectedIds: useMemo(() => selectedRows.map((row) => row?.id), [selectedRows]),
  });

  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    if (!searchValue) {
      setFilteredData([]);
      return;
    }
    const getData = setTimeout(() => {
      filter(searchValue);
    }, 500);

    return () => clearTimeout(getData);
  }, [searchValue, filter, setFilteredData]);

  return (
    <Flex $column>
      <Styled.HeaderContainer>
        <ModalHeader heading={title} />
        <HelperText
          marginBottom={24}
          marginTop={24}
        >
          {"Pick a different ticket (not selected) to merge into."}
        </HelperText>
      </Styled.HeaderContainer>
      <Styled.Container $smallerPadding>
        <Styled.SearchContainer>
          <Styled.Search
            placeholder="Search..."
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.currentTarget.value)}
          />
        </Styled.SearchContainer>

        <Styled.ScrollContainer>
          {loading && <Spinner />}
          {searchValue && !loading && !Boolean(filteredData.length) && (
            <Flex
              $alignItems="center"
              $justifyContent="center"
              style={{ height: "100%" }}
            >
              <Paragraph fontSize={14}>No results to show</Paragraph>
            </Flex>
          )}
          {!searchValue && !Boolean(filteredData.length) && (
            <Flex
              $alignItems="center"
              $justifyContent="center"
              style={{ height: "100%" }}
            >
              <Paragraph fontSize={14}>Start typing to view results</Paragraph>
            </Flex>
          )}
          {!loading && Boolean(filteredData.length) && (
            <Flex $column>
              {filteredData.map((issue: CrashReportTableRowItem | IssueTableRowItem) => {
                return (
                  <Styled.Button
                    onClick={() => {
                      confirmAction(selectedRows, issue as unknown as TData);
                      deselectRows?.();
                      close();
                    }}
                    key={issue.id}
                  >
                    {getStatusIcon(issue.status)}
                    <p
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "35rem",
                      }}
                    >
                      {issue.row_type === RowType.USER_FEEDBACK ? issue.prettyTitle : issue.name}
                    </p>
                  </Styled.Button>
                );
              })}
            </Flex>
          )}
        </Styled.ScrollContainer>
      </Styled.Container>
    </Flex>
  );
}
